import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ImageList,
  ImageListItem,
} from '@mui/material';
import {
  Visibility,
  Link,
  Twitter,
  Facebook,
  Instagram,
  VideoLibrary,
  FilterList,
  Download,
  Refresh,
  CheckCircle,
  Cancel,
  Schedule,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { socialMediaApi } from '../../api/socialMediaApi';

interface PostRecord {
  postId: string;
  platform: string;
  contentType: string;
  status: 'posted' | 'failed' | 'scheduled' | 'cancelled';
  postedAt?: number;
  scheduledFor?: number;
  content: {
    text?: string;
    caption?: string;
    images?: string[];
    videoUrl?: string;
  };
  metrics?: {
    views?: number;
    likes?: number;
    shares?: number;
    comments?: number;
    clicks?: number;
  };
  platformPostId?: string;
  postUrl?: string;
  errorMessage?: string;
}

interface PostHistoryProps {
  onAlert: (type: 'success' | 'error' | 'info', message: string) => void;
}

interface FilterState {
  platform: string;
  status: string;
  contentType: string;
  dateFrom: dayjs.Dayjs | null;
  dateTo: dayjs.Dayjs | null;
}

const PostHistory: React.FC<PostHistoryProps> = ({ onAlert }) => {
  const [posts, setPosts] = useState<PostRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPosts, setTotalPosts] = useState(0);
  const [selectedPost, setSelectedPost] = useState<PostRecord | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    platform: '',
    status: '',
    contentType: '',
    dateFrom: null,
    dateTo: null,
  });
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    platform: '',
    status: '',
    contentType: '',
    dateFrom: null,
    dateTo: null,
  });

  useEffect(() => {
    fetchPosts();
  }, [page, rowsPerPage, activeFilters]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await socialMediaApi.getPostHistory({
        page: page + 1,
        limit: rowsPerPage,
        ...activeFilters,
        dateFrom: activeFilters.dateFrom?.toISOString(),
        dateTo: activeFilters.dateTo?.toISOString(),
      });
      setPosts(response.posts);
      setTotalPosts(response.total);
    } catch (error) {
      onAlert('error', 'Failed to fetch post history');
      console.error('Error fetching post history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (post: PostRecord) => {
    setSelectedPost(post);
    setDetailDialogOpen(true);
  };

  const handleApplyFilters = () => {
    setActiveFilters(filters);
    setPage(0);
    setFilterDialogOpen(false);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      platform: '',
      status: '',
      contentType: '',
      dateFrom: null,
      dateTo: null,
    };
    setFilters(resetFilters);
    setActiveFilters(resetFilters);
    setPage(0);
    setFilterDialogOpen(false);
  };

  const handleExportData = async () => {
    try {
      const response = await socialMediaApi.exportHistory({
        ...activeFilters,
        dateFrom: activeFilters.dateFrom?.toISOString(),
        dateTo: activeFilters.dateTo?.toISOString(),
      });
      const blob = response; // exportHistory already returns a blob
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `post-history-${dayjs().format('YYYY-MM-DD')}.csv`;
      link.click();
      onAlert('success', 'Export downloaded successfully');
    } catch (error) {
      onAlert('error', 'Failed to export data');
      console.error('Error exporting data:', error);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
        return <Twitter sx={{ color: '#1DA1F2' }} />;
      case 'facebook':
        return <Facebook sx={{ color: '#1877F2' }} />;
      case 'instagram':
        return <Instagram sx={{ color: '#E4405F' }} />;
      case 'tiktok':
        return <VideoLibrary sx={{ color: '#000000' }} />;
      default:
        return null;
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      posted: { color: 'success' as const, icon: <CheckCircle fontSize="small" /> },
      failed: { color: 'error' as const, icon: <Cancel fontSize="small" /> },
      scheduled: { color: 'info' as const, icon: <Schedule fontSize="small" /> },
      cancelled: { color: 'default' as const, icon: <Cancel fontSize="small" /> },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.cancelled;

    return (
      <Chip
        label={status}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const hasActiveFilters = () => {
    return (
      activeFilters.platform ||
      activeFilters.status ||
      activeFilters.contentType ||
      activeFilters.dateFrom ||
      activeFilters.dateTo
    );
  };

  if (loading && posts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Post History
            {hasActiveFilters() && (
              <Chip label="Filtered" size="small" sx={{ ml: 1 }} onDelete={handleResetFilters} />
            )}
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              startIcon={<FilterList />}
              onClick={() => setFilterDialogOpen(true)}
              variant="outlined"
            >
              Filter
            </Button>
            <Button
              startIcon={<Download />}
              onClick={handleExportData}
              variant="outlined"
            >
              Export
            </Button>
            <Button
              startIcon={<Refresh />}
              onClick={fetchPosts}
              variant="outlined"
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Platform</TableCell>
                <TableCell>Content Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Engagement</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.map((post) => (
                <TableRow key={post.postId} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getPlatformIcon(post.platform)}
                      <Typography sx={{ ml: 1 }}>{post.platform}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{post.contentType}</TableCell>
                  <TableCell>{getStatusChip(post.status)}</TableCell>
                  <TableCell>
                    {post.postedAt
                      ? dayjs(post.postedAt).format('MMM DD, YYYY HH:mm')
                      : post.scheduledFor
                      ? dayjs(post.scheduledFor).format('MMM DD, YYYY HH:mm')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {post.metrics ? (
                      <Box display="flex" gap={1}>
                        {post.metrics.likes !== undefined && (
                          <Typography variant="caption">
                            {post.metrics.likes} likes
                          </Typography>
                        )}
                        {post.metrics.shares !== undefined && (
                          <Typography variant="caption">
                            {post.metrics.shares} shares
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetails(post)}
                    >
                      <Visibility />
                    </IconButton>
                    {post.postUrl && (
                      <IconButton
                        size="small"
                        component="a"
                        href={post.postUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Link />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalPosts}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>

        {/* Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Post Details
          </DialogTitle>
          <DialogContent>
            {selectedPost && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getPlatformIcon(selectedPost.platform)}
                    <Typography variant="h6">{selectedPost.platform}</Typography>
                    {getStatusChip(selectedPost.status)}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedPost.postedAt
                      ? `Posted: ${dayjs(selectedPost.postedAt).format('MMM DD, YYYY HH:mm')}`
                      : selectedPost.scheduledFor
                      ? `Scheduled: ${dayjs(selectedPost.scheduledFor).format('MMM DD, YYYY HH:mm')}`
                      : ''}
                  </Typography>
                </Grid>

                {selectedPost.content.text && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Content
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedPost.content.text || selectedPost.content.caption}
                    </Typography>
                  </Grid>
                )}

                {selectedPost.content.images && selectedPost.content.images.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Images
                    </Typography>
                    <ImageList cols={3} gap={8}>
                      {selectedPost.content.images.map((image, index) => (
                        <ImageListItem key={index}>
                          <img
                            src={image}
                            alt={`Image ${index + 1}`}
                            loading="lazy"
                            style={{ maxWidth: '100%', height: 'auto' }}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Grid>
                )}

                {selectedPost.metrics && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Engagement Metrics
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedPost.metrics.views !== undefined && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Views</Typography>
                          <Typography variant="h6">{selectedPost.metrics.views}</Typography>
                        </Grid>
                      )}
                      {selectedPost.metrics.likes !== undefined && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Likes</Typography>
                          <Typography variant="h6">{selectedPost.metrics.likes}</Typography>
                        </Grid>
                      )}
                      {selectedPost.metrics.shares !== undefined && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Shares</Typography>
                          <Typography variant="h6">{selectedPost.metrics.shares}</Typography>
                        </Grid>
                      )}
                      {selectedPost.metrics.comments !== undefined && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Comments</Typography>
                          <Typography variant="h6">{selectedPost.metrics.comments}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}

                {selectedPost.errorMessage && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error">
                      Error Message
                    </Typography>
                    <Typography variant="body2">{selectedPost.errorMessage}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            {selectedPost?.postUrl && (
              <Button
                component="a"
                href={selectedPost.postUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<Link />}
              >
                View on Platform
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Filter Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Filter Posts</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={filters.platform}
                    onChange={(e) => setFilters({ ...filters, platform: e.target.value })}
                    label="Platform"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="twitter">Twitter</MenuItem>
                    <MenuItem value="facebook">Facebook</MenuItem>
                    <MenuItem value="instagram">Instagram</MenuItem>
                    <MenuItem value="tiktok">TikTok</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="posted">Posted</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Content Type</InputLabel>
                  <Select
                    value={filters.contentType}
                    onChange={(e) => setFilters({ ...filters, contentType: e.target.value })}
                    label="Content Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="daily-reveal">Daily Reveal</MenuItem>
                    <MenuItem value="carousel">Carousel</MenuItem>
                    <MenuItem value="leaderboard">Leaderboard</MenuItem>
                    <MenuItem value="teaser">Teaser</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="From Date"
                  value={filters.dateFrom}
                  onChange={(value) => setFilters({ ...filters, dateFrom: value })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="To Date"
                  value={filters.dateTo}
                  onChange={(value) => setFilters({ ...filters, dateTo: value })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleResetFilters}>Reset</Button>
            <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleApplyFilters} variant="contained">
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PostHistory;